// The row of tabs under the header, in the Mac app's workspace only
// (SUN-1155). The tabs are the workspace's (workspace/model.ts): it says which
// there are, and the row asks it for whatever is done to one. A click brings a tab to the front; its ×
// or a middle click closes it; + opens one; a tab dragged along the row is
// put where it is dropped, and one dropped on the other pane's row moves
// there. The context menu on a tab is the shell's own. To the keyboard the
// row is three stops whatever its tabs (the tab in front, its ×, and +):
// from a tab the arrows move along the row, Enter or Space brings the tab to
// the front, and Delete or Backspace closes it.
import { useEffect, useLayoutEffect, useRef, useState, type DragEvent } from 'react'
import { tellShell, type ShellTab, type TabRequest } from '../lib/shell'
import { cx } from '../transcript/bits'
import { CloseIcon, PlusIcon } from './Icons'

/**
 * Where a tab dropped at `x` goes in a row whose tabs are at these places:
 * before the first one whose middle is right of `x`, else at the end (null).
 */
export function dropBefore(tabs: readonly { id: string; left: number; width: number }[], x: number): string | null {
  return tabs.find((tab) => x < tab.left + tab.width / 2)?.id ?? null
}

/**
 * Where a row scrolled to `scrolled` and `room` wide must scroll to so that
 * the tab at `left`, `width` wide, is in view: no further than it has to, and
 * to the tab's start when the tab is wider than the row.
 */
export function scrollToShow(scrolled: number, room: number, left: number, width: number): number {
  if (left < scrolled) return left
  if (left + width > scrolled + room) return Math.min(left, left + width - room)
  return scrolled
}

/**
 * The tab the keyboard goes to from tab `from` of a row of `count`: the
 * arrows move along the row and round it, Home and End go to its ends. Null
 * for any other key.
 */
export function focusMove(key: string, from: number, count: number): number | null {
  if (count <= 0) return null
  switch (key) {
    case 'ArrowLeft':
      return (from - 1 + count) % count
    case 'ArrowRight':
      return (from + 1) % count
    case 'Home':
      return 0
    case 'End':
      return count - 1
    default:
      return null
  }
}

export function PageTabs({ workspace }: { workspace: { tabs: ShellTab[]; dragging: boolean; request(message: TabRequest): void } }) {
  const { tabs, dragging, request } = workspace
  const list = useRef<HTMLDivElement>(null)
  /** The tab of this row being dragged, from its dragstart to its dragend. */
  const [dragged, setDragged] = useState<string | null>(null)
  /** Where what is dragged would land in this row: before that tab, or at the end (null). Undefined while it is not over the row. */
  const [drop, setDrop] = useState<string | null | undefined>(undefined)
  /** The tab the middle button went down on: it closes when the button comes up on it. */
  const pressed = useRef<string | null>(null)
  // A row takes a drop only while a tab is being dragged: one of its own, or
  // one of the other pane's, which the workspace says.
  const accepting = dragging || dragged !== null
  const active = tabs.find((tab) => tab.active)?.id
  const last = tabs[tabs.length - 1]?.id
  // The one tab the Tab key stops on: the one in front. The arrows reach the
  // others from it.
  const stop = active ?? tabs[0]?.id

  // The tab in front stays in view when the row has more tabs than room: as
  // another comes to the front, as their number changes, and as the row gets
  // narrower with its window or its pane.
  useLayoutEffect(() => {
    const row = list.current
    if (!row) return
    const show = () => {
      const tab = row.querySelector<HTMLElement>('.page-tab.is-active')
      if (!tab) return
      const to = scrollToShow(row.scrollLeft, row.clientWidth, tab.offsetLeft, tab.offsetWidth)
      if (to !== row.scrollLeft) row.scrollLeft = to
    }
    show()
    if (typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(show)
    observer.observe(row)
    return () => observer.disconnect()
  }, [active, tabs.length])

  // The workspace says when a drag is over: a drop on a row ends it there. A tab
  // dropped on the other pane's row has left this one, and its own end may
  // never be said here.
  useEffect(() => {
    if (!dragging) setDragged(null)
  }, [dragging])

  // The drag ended, here or in the other pane: no place to show any more.
  useEffect(() => {
    if (!accepting) setDrop(undefined)
  }, [accepting])

  const placeAt = (x: number): string | null => {
    const places = [...(list.current?.querySelectorAll<HTMLElement>('[data-tab-id]') ?? [])].map((tab) => {
      const { left, width } = tab.getBoundingClientRect()
      return { id: tab.dataset.tabId ?? '', left, width }
    })
    return dropBefore(places, x)
  }

  const onDragOver = (event: DragEvent<HTMLDivElement>) => {
    if (!accepting) return
    event.preventDefault()
    event.dataTransfer.dropEffect = 'move'
    setDrop(placeAt(event.clientX))
  }

  const onDragLeave = (event: DragEvent<HTMLDivElement>) => {
    // It also fires as the pointer passes from one tab of the row to the
    // next: only out of the row is the place gone.
    const { left, right, top, bottom } = event.currentTarget.getBoundingClientRect()
    const inside = event.clientX >= left && event.clientX < right && event.clientY >= top && event.clientY < bottom
    if (!inside) setDrop(undefined)
  }

  const onDrop = (event: DragEvent<HTMLDivElement>) => {
    if (!accepting) return
    event.preventDefault()
    setDrop(undefined)
    request({ drop: { before: placeAt(event.clientX) } })
  }

  const onDragStart = (event: DragEvent<HTMLDivElement>, tab: ShellTab) => {
    event.dataTransfer.effectAllowed = 'move'
    // What another app gets when the tab is dropped on it. The workspace knows
    // which tab is dragged: no row reads this.
    event.dataTransfer.setData('text/plain', tab.href || window.location.href)
    setDragged(tab.id)
    request({ drag: tab.id })
    // On the element itself: a tab that moved to the other pane has left
    // this row by the time its drag ends, and no longer reaches a handler of
    // the row's.
    event.currentTarget.addEventListener(
      'dragend',
      () => {
        setDragged(null)
        request({ drag: null })
      },
      { once: true },
    )
  }

  const select = (tab: ShellTab) => {
    if (!tab.active) request({ select: tab.id })
  }

  return (
    <div className="page-tabs" onDragOver={onDragOver} onDragLeave={onDragLeave} onDrop={onDrop}>
      <div className="page-tabs-list" role="tablist" aria-label="Tabs" ref={list}>
        {tabs.map((tab, i) => (
          <div
            key={tab.id}
            role="presentation"
            className={cx(
              'page-tab',
              tab.active && 'is-active',
              dragged === tab.id && 'is-dragged',
              drop === tab.id && 'is-drop-before',
              drop === null && tab.id === last && 'is-drop-after',
            )}
            data-tab-id={tab.id}
            draggable
            onDragStart={(event) => onDragStart(event, tab)}
            onClick={() => select(tab)}
            onMouseDown={(event) => {
              if (event.button !== 1) return
              // The middle button closes the tab, and does nothing else.
              event.preventDefault()
              pressed.current = tab.id
            }}
            onMouseUp={(event) => {
              if (event.button !== 1) return
              if (pressed.current === tab.id) request({ close: tab.id })
              pressed.current = null
            }}
            onContextMenu={() => { tellShell({ workspaceContext: { tab: tab.id } }); tellShell({ menuTab: tab.id }) }}
          >
            <span
              className="page-tab-title"
              role="tab"
              aria-selected={tab.active}
              tabIndex={tab.id === stop ? 0 : -1}
              title={tab.title}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault()
                  select(tab)
                  return
                }
                if (event.key === 'Delete' || event.key === 'Backspace') {
                  event.preventDefault()
                  request({ close: tab.id })
                  return
                }
                const to = focusMove(event.key, i, tabs.length)
                if (to === null) return
                event.preventDefault()
                list.current?.querySelectorAll<HTMLElement>('[role="tab"]')[to]?.focus()
              }}
            >
              {tab.title}
            </span>
            <button
              type="button"
              className="page-tab-close"
              // Named after its tab: a list of the row's buttons tells them apart.
              aria-label={`Close tab: ${tab.title}`}
              // By the keyboard a tab closes with Delete; only the × of the tab in front is a stop.
              tabIndex={tab.id === stop ? 0 : -1}
              title="Close tab"
              onClick={(event) => {
                event.stopPropagation()
                request({ close: tab.id })
              }}
            >
              <CloseIcon size={12} />
            </button>
          </div>
        ))}
      </div>
      <button type="button" className="page-tabs-new" aria-label="New tab" title="New tab" onClick={() => request({ new: true })}>
        <PlusIcon size={14} />
      </button>
    </div>
  )
}
